import React, { useState } from 'react';
import './CartSlider.css';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import LoadingButton from './LoadingButton';
import { resolveImageUrl } from '../utils/image';

type CartSliderProps = {
  open: boolean;
  onClose: () => void;
};

export default function CartSlider({ open, onClose }: CartSliderProps) {
  const { cart, updateQuantity, removeFromCart } = useCart();
  const navigate = useNavigate();
  const [updatingQuantity, setUpdatingQuantity] = useState<{ [key: string]: boolean }>({});

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const MIN_ORDER_VALUE = 2000;
  const isMinOrderMet = subtotal >= MIN_ORDER_VALUE;
  const amountNeeded = Math.max(0, MIN_ORDER_VALUE - subtotal);
  const progressPercent = Math.min(100, Math.round((subtotal / MIN_ORDER_VALUE) * 100));

  const handleQuantityUpdate = async (itemId: string, newQuantity: number) => {
    setUpdatingQuantity(prev => ({ ...prev, [itemId]: true }));
    try {
      await updateQuantity(itemId, newQuantity);
    } catch (error) {
      console.error('Failed to update quantity:', error);
    } finally {
      setUpdatingQuantity(prev => ({ ...prev, [itemId]: false }));
    }
  };

  return (
    <>
      {open && <div className="cart-slider-backdrop" onClick={onClose} />}
      <div className={`cart-slider${open ? " open" : ""}`}>
        {/* Header */}
        <div className="cart-slider-header">
          <div className="cart-slider-title-wrap">
            <span className="cart-slider-icon">🛒</span>
            <span className="cart-slider-title">Your Cart</span>
            <span className="cart-slider-badge">{cart.length}</span>
          </div>
          <button className="cart-slider-close" onClick={onClose} aria-label="Close cart">
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="cart-slider-body">
          {cart.length === 0 ? (
            <div className="slider-empty-cart">
              <div className="slider-empty-icon">🎆</div>
              <h3>Your cart is empty</h3>
              <p>Add some festive fireworks to see them here!</p>
              <button 
                className="slider-browse-btn" 
                onClick={() => { onClose(); navigate('/category'); }}
              >
                Browse Fireworks
              </button>
            </div>
          ) : (
            <div className="slider-items-list">
              {cart.map(item => (
                <div className="slider-item" key={item.id}>
                  <div className="slider-item-img-wrap">
                    {item.imageUrl ? (
                      <img src={resolveImageUrl(item.imageUrl)} alt={item.name} className="slider-item-img" />
                    ) : (
                      <div className="slider-item-placeholder">🧨</div>
                    )}
                  </div>
                  <div className="slider-item-info">
                    <div className="slider-item-name">{item.name}</div>
                    <div className="slider-item-price-unit">₹{item.price.toFixed(2)} each</div>
                    
                    <div className="slider-item-controls">
                      <div className="slider-stepper">
                        <LoadingButton
                          onClick={() => handleQuantityUpdate(item.id, item.quantity - 1)}
                          loading={updatingQuantity[item.id] || false}
                          disabled={item.quantity <= 1}
                          className="slider-step-btn"
                        >
                          -
                        </LoadingButton>
                        <span className="slider-step-val">{item.quantity}</span>
                        <LoadingButton
                          onClick={() => handleQuantityUpdate(item.id, item.quantity + 1)}
                          loading={updatingQuantity[item.id] || false}
                          className="slider-step-btn"
                        >
                          +
                        </LoadingButton>
                      </div>

                      <div className="slider-item-subtotal">
                        ₹{(item.price * item.quantity).toFixed(2)}
                      </div>

                      <button 
                        className="slider-item-remove" 
                        onClick={() => removeFromCart(item.id)}
                        title="Remove"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cart.length > 0 && (
          <div className="cart-slider-footer">
            <div className="slider-subtotal-box">
              <div className="slider-subtotal-row">
                <span>Subtotal:</span>
                <span className="slider-subtotal-val">₹{subtotal.toFixed(2)}</span>
              </div>
              <div className="slider-discount-note">✨ 50% wholesale festival discount included</div>
            </div>

            {/* Minimum Order Threshold Progress Bar */}
            <div className={`slider-min-order-box ${isMinOrderMet ? 'met' : 'unmet'}`}>
              <div className="slider-min-order-header">
                <span>{isMinOrderMet ? '✅ Min. order ₹2,000 reached' : `⚡ Add ₹${amountNeeded.toFixed(0)} for min. order`}</span>
                <span className="slider-min-order-percent">{progressPercent}%</span>
              </div>
              <div className="slider-min-progress-track">
                <div className="slider-min-progress-fill" style={{ width: `${progressPercent}%` }}></div>
              </div>
            </div>

            <div style={{ fontSize: '11px', color: '#64748b', textAlign: 'center', margin: '6px 0', lineHeight: 1.4 }}>
              ⚖️ Offline Enquiry Booking Only (Supreme Court 2018 Compliant)
            </div>

            <div className="cart-slider-buttons">
              <button
                className="slider-view-cart-btn"
                onClick={() => {
                  onClose();
                  navigate('/cart');
                }}
              >
                View Full Cart
              </button>
              <button
                className={`slider-checkout-btn ${!isMinOrderMet ? 'disabled' : ''}`}
                disabled={!isMinOrderMet}
                onClick={() => {
                  if (!isMinOrderMet) {
                    alert(`Minimum order value is ₹${MIN_ORDER_VALUE}. Please add ₹${amountNeeded.toFixed(2)} more to checkout.`);
                    return;
                  }
                  onClose();
                  navigate('/checkout');
                }}
                title={!isMinOrderMet ? `Add ₹${amountNeeded.toFixed(2)} more to checkout` : 'Proceed to Checkout'}
              >
                {isMinOrderMet ? 'Checkout →' : `Add ₹${amountNeeded.toFixed(0)}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}